import React from 'react';
import { Card, Badge } from 'nelitomorphism';
import './Experience.css';

// Dates follow the CV. Company links were resolved live — entries without a
// `url` had no reachable domain (Ahto.digital is gone), and Bulldesk now
// redirects to Five Labs, so it points at the successor company.
const ROLES = [
  {
    role: 'Full-stack developer',
    company: 'Fuel Network',
    url: 'https://fuel.network',
    period: 'Sep 2024 — present',
    duration: '1y 10mo',
    location: 'Remote',
    current: true,
    summary:
      'Edge tooling for the Fuel blockchain: a chain explorer, an indexer, a Chrome extension wallet, and the projects that support them.',
    skills: ['React.js', 'Node.js', 'Blockchain', 'Ethereum', 'CI/CD', 'AWS', 'Docker'],
  },
  {
    role: 'Full-stack developer',
    company: 'Mind The Graph',
    url: 'https://mindthegraph.com',
    period: 'Jul 2022 — Sep 2024',
    duration: '2y 3mo',
    location: 'Remote',
    summary:
      'Moved the codebase from PHP/jQuery to React and Node.js while the product stayed live. Fully async company.',
    skills: ['React.js', 'Node.js', 'Nest.js', 'CodeIgniter', 'AWS', 'S3'],
  },
  {
    role: 'Full-stack developer · Solutions Engineer',
    company: 'Duda — Snipcart',
    url: 'https://www.duda.co',
    period: 'Jan 2022 — Jul 2022',
    duration: '7mo',
    location: 'Florianópolis · Remote (California / Quebec)',
    summary:
      'Split across both companies through the Snipcart acquisition, aiding the merge and building internal tools focused on LATAM clients.',
    skills: ['Vue.js', '.NET', 'Azure', 'Scrum'],
  },
  {
    role: 'Full-stack developer',
    company: 'Bulldesk',
    url: 'https://fivelabs.com.br',
    period: 'Jun 2021 — Jan 2022',
    duration: '8mo',
    location: 'Florianópolis',
    summary:
      'Built the main product in Laravel and Vue.js in a test-driven environment, plus side projects on Slim PHP and Angular, run with Scrum.',
    skills: ['Vue.js', 'Laravel', 'PHPUnit', 'PostgreSQL', 'MySQL', 'CI/CD'],
  },
  {
    role: 'Tech Support Engineer',
    company: 'Duda',
    url: 'https://www.duda.co',
    period: 'Jan 2020 — Jun 2021',
    duration: '1y 6mo',
    location: 'Florianópolis · Remote (California)',
    summary:
      'Supported customers building on the platform and wrote custom solutions for the team: task automation, API integrations, custom widgets.',
    skills: ['JavaScript', 'jQuery', 'Rest API'],
  },
  {
    role: 'Full-time freelance developer',
    company: 'Independent · Ahto.digital',
    period: 'Jan 2019 — Jan 2020',
    duration: '1y',
    location: 'Florianópolis',
    summary:
      'Developed and hosted sites across a range of stacks, paired with a team of designers and marketers under the agency Ahto.digital.',
    skills: ['PHP', 'Laravel', 'WordPress', 'Linux', 'LAMP'],
  },
  {
    role: 'Full-stack developer',
    company: 'Clint.digital',
    url: 'https://www.useclint.com',
    period: 'Jan 2018 — Dec 2018',
    duration: '1y',
    location: 'Florianópolis',
    summary:
      'Web development for agency clients plus DevOps, Linux server management and networking, through the company’s transition from agency to software platform.',
    skills: ['WordPress', 'PHP', 'Laravel', 'Linux', 'jQuery', 'Hugo'],
  },
  {
    role: 'Intern',
    company: 'Prefeitura Municipal de São José',
    url: 'https://saojose.sc.gov.br',
    period: 'Mar 2017 — Jan 2018',
    duration: '11mo',
    location: 'Florianópolis',
    intern: true,
    summary:
      'I.T. department: network management and maintenance, software and hardware support.',
    skills: ['Networking', 'Windows'],
  },
  {
    role: 'Intern',
    company: 'University of Debrecen',
    url: 'https://unideb.hu',
    period: 'Jun 2015 — Aug 2015',
    duration: '3mo',
    location: 'Debrecen, Hungary',
    intern: true,
    summary:
      'Upkeep of university websites and database work, closing a one-year exchange program.',
    skills: ['MySQL', 'Web Development'],
  },
  {
    role: 'Intern',
    company: 'Lelak Hipermídia',
    url: 'https://www.lelak.co',
    period: 'Mar 2013 — Dec 2013',
    duration: '10mo',
    location: 'Florianópolis',
    intern: true,
    summary:
      'Joined to work on a legacy Kohler site in Java Web, then went deep on jQuery, pixel-perfect HTML email, WordPress and CakePHP.',
    skills: ['Java Web', 'PHP', 'CakePHP', 'WordPress', 'jQuery'],
  },
];

const Experience = () => (
  <section id="experience">
    <Card title="Experience" meta="2013 — present">
      <ol className="xp-list">
        {ROLES.map(r => (
          <li key={`${r.company}-${r.period}`} className="xp-item">
            <div className="xp-rail" aria-hidden="true">
              <span className={r.current ? 'xp-dot xp-dot-current' : 'xp-dot'} />
            </div>
            <div className="xp-body">
              <div className="xp-head">
                <h3 className="xp-role">{r.role}</h3>
                {r.current && (
                  <Badge tone="ok" dot pulse>
                    Current
                  </Badge>
                )}
                {r.intern && <Badge>Internship</Badge>}
              </div>
              <p className="xp-company">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer">
                    {r.company} ↗
                  </a>
                ) : (
                  r.company
                )}
              </p>
              <p className="xp-meta">
                {r.period} · {r.duration}
                {r.location ? ` · ${r.location}` : ''}
              </p>
              <p className="xp-summary">{r.summary}</p>
              {r.skills && (
                <div className="xp-skills">
                  {r.skills.map(s => (
                    <Badge key={s}>{s}</Badge>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  </section>
);

export default Experience;
